/**
 * Calendar sync integration row (#1715, epic #2344): one-way export of the
 * upcoming care schedule into a writable device calendar. Setup walks
 * consent → OS permission → calendar choice; a connected row exposes sync
 * status, manual re-sync, retry, and disconnect (which removes every
 * Louise-created event).
 */

import { useCallback, useMemo, useState } from "react";
import {
  ActivityIndicator,
  Linking,
  Text,
  View,
  type ImageSourcePropType,
} from "react-native";
import {
  ActionBanner,
  CompactActionButton,
  Dialog,
  DialogActions,
  DialogPrimaryButton,
  MoonRadio,
  SoftSecondaryButton,
} from "@louise/moon-ui";
import {
  useCalendarSync,
  type WritableDeviceCalendar,
} from "@src/packages/calendar-sync";
import { formatDateTime, useLocale, useTranslations } from "@src/packages/i18n";
import { EMBER } from "../../../../onboarding/theme";
import { IntegrationRow } from "./IntegrationRow";

const CALENDAR_LOGO =
  require("../../../../assets/images/you-icons/calendar.png") as ImageSourcePropType;

type SetupStep =
  | "closed"
  | "consent"
  | "choose_calendar"
  | "permission_denied"
  | "no_calendars"
  | "manage";

function DialogHeading({ children }: { children: string }) {
  return (
    <Text
      className="font-dm-sans"
      style={{
        fontSize: 17,
        lineHeight: 22,
        fontWeight: "600",
        color: EMBER.colors.text,
        marginBottom: 10,
      }}
    >
      {children}
    </Text>
  );
}

function DialogBody({ children }: { children: string }) {
  return (
    <Text
      className="font-dm-sans"
      style={{
        fontSize: 14,
        lineHeight: 20,
        color: EMBER.colors.textMid,
        marginBottom: 20,
      }}
    >
      {children}
    </Text>
  );
}

export default function CalendarSyncIntegration() {
  const t = useTranslations();
  const { locale } = useLocale();
  const sync = useCalendarSync({
    appointmentFallbackTitle: t("you.calendar_sync.untitled_appointment"),
    medicationFallbackTitle: t("you.calendar_sync.unnamed_medication"),
    onlineLocationLabel: t("you.calendar_sync.online_appointment_location"),
  });

  const [step, setStep] = useState<SetupStep>("closed");
  const [calendars, setCalendars] = useState<WritableDeviceCalendar[]>([]);
  const [selectedCalendarId, setSelectedCalendarId] = useState<string | null>(
    null,
  );
  const [isPreparing, setIsPreparing] = useState(false);
  const [flowFailed, setFlowFailed] = useState(false);

  const closeDialog = useCallback(() => {
    setStep("closed");
    setFlowFailed(false);
  }, []);

  const beginSetup = useCallback(() => {
    setFlowFailed(false);
    setStep("consent");
  }, []);

  const continueFromConsent = useCallback(async () => {
    setIsPreparing(true);
    setFlowFailed(false);
    try {
      const permission = await sync.requestPermission();
      if (permission !== "granted") {
        setStep("permission_denied");
        return;
      }
      const writable = await sync.listWritableCalendars();
      if (writable.length === 0) {
        setStep("no_calendars");
        return;
      }
      setCalendars(writable);
      setSelectedCalendarId(writable[0]?.id ?? null);
      setStep("choose_calendar");
    } catch {
      setFlowFailed(true);
    } finally {
      setIsPreparing(false);
    }
  }, [sync]);

  const confirmCalendar = useCallback(async () => {
    const calendar = calendars.find((c) => c.id === selectedCalendarId);
    if (!calendar) return;
    setIsPreparing(true);
    setFlowFailed(false);
    try {
      await sync.connect(calendar);
      closeDialog();
    } catch {
      setFlowFailed(true);
    } finally {
      setIsPreparing(false);
    }
  }, [calendars, selectedCalendarId, sync, closeDialog]);

  const syncNow = useCallback(async () => {
    try {
      await sync.syncNow();
    } catch {
      // Surfaced through sync.syncError below.
    }
  }, [sync]);

  const disconnect = useCallback(async () => {
    try {
      await sync.disconnect();
      closeDialog();
    } catch {
      setFlowFailed(true);
    }
  }, [sync, closeDialog]);

  // Destructured so the memos below depend on stable values instead of the
  // hook's per-render return object (risk-assessment note on PR #3294).
  const {
    isSupported,
    isStateLoading,
    isConnected,
    isSyncing,
    isDisconnecting,
    syncError,
    state: syncState,
  } = sync;

  const syncedEventCount = useMemo(
    () => (syncState ? Object.keys(syncState.events).length : 0),
    [syncState],
  );

  const subtitle = useMemo(() => {
    if (!isSupported || !isConnected) {
      return t("you.calendar_sync.add_care_events_to_device_calendar");
    }
    if (isSyncing) return t("you.calendar_sync.sync_in_progress");
    if (syncError) return t("you.calendar_sync.sync_failed_try_again");
    if (syncState?.lastSyncFailedCount) {
      return t("you.calendar_sync.partial_sync_failure_retry");
    }
    if (syncState?.lastSyncedAt) {
      return t("you.calendar_sync.last_synced_at", {
        params: { when: formatDateTime(syncState.lastSyncedAt, locale) },
      });
    }
    return t("you.calendar_sync.not_synced_yet");
  }, [isSupported, isConnected, isSyncing, syncError, syncState, t, locale]);

  const trailing = useMemo(() => {
    if (!isSupported) {
      return (
        <Text
          className="font-dm-sans"
          style={{
            fontSize: 12,
            lineHeight: 16,
            color: EMBER.colors.textFaint,
            fontWeight: "500",
          }}
        >
          {t("you.calendar_sync.available_in_mobile_app")}
        </Text>
      );
    }
    if (isStateLoading) return null;
    if (!isConnected) {
      return (
        <CompactActionButton
          label={t("you.calendar_sync.set_up_sync")}
          size="sm"
          variant="primary"
          onPress={beginSetup}
        />
      );
    }
    return (
      <CompactActionButton
        label={t("you.calendar_sync.sync_now")}
        size="sm"
        variant="primary"
        loading={isSyncing}
        onPress={() => void syncNow()}
      />
    );
  }, [
    isSupported,
    isStateLoading,
    isConnected,
    isSyncing,
    t,
    beginSetup,
    syncNow,
  ]);

  return (
    <>
      <IntegrationRow
        source={CALENDAR_LOGO}
        title="Calendar sync"
        subtitle={subtitle}
        trailing={trailing}
        onPress={
          isSupported && isConnected ? () => setStep("manage") : undefined
        }
        testID="calendar-sync-row"
      />

      <Dialog
        visible={step !== "closed"}
        onClose={closeDialog}
        layout="centered"
        dismissOnBackdropTap={!isPreparing && !isDisconnecting}
        testID="calendar-sync-dialog"
      >
        {step === "consent" ? (
          <View>
            <DialogHeading>
              {t("you.calendar_sync.before_you_connect")}
            </DialogHeading>
            <DialogBody>
              {t("you.calendar_sync.events_visible_to_calendar_viewers")}
            </DialogBody>
            {flowFailed ? (
              <View style={{ marginBottom: 16 }}>
                <ActionBanner
                  variant="important"
                  message={t("you.calendar_sync.sync_failed_try_again")}
                />
              </View>
            ) : null}
            <DialogActions>
              <SoftSecondaryButton
                label={t("you.calendar_sync.not_now")}
                onPress={closeDialog}
                flex={1}
              />
              <DialogPrimaryButton
                label={t("you.calendar_sync.continue_and_allow_access")}
                onPress={() => void continueFromConsent()}
                loading={isPreparing}
                flex={1}
              />
            </DialogActions>
          </View>
        ) : null}

        {step === "choose_calendar" ? (
          <View>
            <DialogHeading>
              {t("you.calendar_sync.choose_a_calendar")}
            </DialogHeading>
            <DialogBody>
              {t("you.calendar_sync.only_writable_calendars_listed")}
            </DialogBody>
            <View style={{ gap: 14, marginBottom: 20 }}>
              {calendars.map((calendar) => (
                <MoonRadio
                  key={calendar.id}
                  value={calendar.id === selectedCalendarId}
                  onChange={() => setSelectedCalendarId(calendar.id)}
                  label={
                    calendar.sourceName
                      ? `${calendar.title} · ${calendar.sourceName}`
                      : calendar.title
                  }
                  accentColor={calendar.color || undefined}
                  testID={`calendar-sync-option-${calendar.id}`}
                />
              ))}
            </View>
            {flowFailed ? (
              <View style={{ marginBottom: 16 }}>
                <ActionBanner
                  variant="important"
                  message={t("you.calendar_sync.sync_failed_try_again")}
                />
              </View>
            ) : null}
            <DialogActions>
              <SoftSecondaryButton
                label={t("you.calendar_sync.not_now")}
                onPress={closeDialog}
                flex={1}
              />
              <DialogPrimaryButton
                label={t("you.calendar_sync.add_events_to_this_calendar")}
                onPress={() => void confirmCalendar()}
                loading={isPreparing}
                disabled={!selectedCalendarId}
                flex={1}
              />
            </DialogActions>
          </View>
        ) : null}

        {step === "permission_denied" ? (
          <View>
            <DialogHeading>
              {t("you.calendar_sync.calendar_access_needed")}
            </DialogHeading>
            <DialogBody>
              {t("you.calendar_sync.calendar_access_denied_open_settings")}
            </DialogBody>
            <DialogActions>
              <SoftSecondaryButton
                label={t("you.calendar_sync.not_now")}
                onPress={closeDialog}
                flex={1}
              />
              <DialogPrimaryButton
                label={t("you.calendar_sync.open_device_settings")}
                onPress={() => void Linking.openSettings()}
                flex={1}
              />
            </DialogActions>
          </View>
        ) : null}

        {step === "no_calendars" ? (
          <View>
            <DialogHeading>
              {t("you.calendar_sync.choose_a_calendar")}
            </DialogHeading>
            <DialogBody>
              {t("you.calendar_sync.no_writable_calendars_found")}
            </DialogBody>
            <DialogActions>
              <DialogPrimaryButton
                label={t("you.calendar_sync.not_now")}
                onPress={closeDialog}
                flex={1}
              />
            </DialogActions>
          </View>
        ) : null}

        {step === "manage" && syncState ? (
          <View>
            <DialogHeading>
              {t("you.calendar_sync.connected_calendar")}
            </DialogHeading>
            <DialogBody>
              {`${syncState.calendarTitle}\n${t(
                "you.calendar_sync.upcoming_events_in_calendar",
                { params: { count: String(syncedEventCount) } },
              )}`}
            </DialogBody>
            {syncState.lastSyncFailedCount > 0 ? (
              <View style={{ marginBottom: 16 }}>
                <ActionBanner
                  variant="important"
                  message={t("you.calendar_sync.partial_sync_failure_retry")}
                  actionLabel={t("you.calendar_sync.sync_now")}
                  onAction={() => void syncNow()}
                />
              </View>
            ) : null}
            {flowFailed ? (
              <View style={{ marginBottom: 16 }}>
                <ActionBanner
                  variant="important"
                  message={t("you.calendar_sync.sync_failed_try_again")}
                />
              </View>
            ) : null}
            {isSyncing || isDisconnecting ? (
              <View style={{ marginBottom: 16, alignItems: "center" }}>
                <ActivityIndicator color={EMBER.colors.warm} />
              </View>
            ) : null}
            <DialogActions>
              <SoftSecondaryButton
                label={t("you.calendar_sync.disconnect_and_remove_events")}
                onPress={() => void disconnect()}
                disabled={isSyncing || isDisconnecting}
                flex={1}
              />
              <DialogPrimaryButton
                label={t("you.calendar_sync.sync_now")}
                onPress={() => void syncNow()}
                loading={isSyncing}
                disabled={isDisconnecting}
                flex={1}
              />
            </DialogActions>
          </View>
        ) : null}
      </Dialog>
    </>
  );
}
